import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, ClipboardCheck, Clock, AlertTriangle, ChevronRight, TrendingUp, 
  UserPlus, Building2, Target, Award, BarChart3, PieChart as PieChartIcon,
  Calendar, Cake, CalendarDays, FileText, MessageSquare, Download
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Progress } from '../../ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { db } from '../../../lib/db';
import { formatDate, getDaysUntil, isOverdue, getUpcomingBirthdays, getUpcomingAnniversaries, getYearsOfService } from '../../../lib/dateUtils';
import { formatStatus, getStatusBadgeColor } from '../../../lib/calculations';
import { useAuthStore } from '../../../store/authStore';
import { 
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, 
  PieChart, Pie, Cell, Legend, LineChart, Line, AreaChart, Area,
  CartesianGrid
} from 'recharts';

const COLORS = ['#0d9488', '#f59e0b', '#8b5cf6', '#10b981', '#ef4444', '#6366f1', '#ec4899'];

export default function CEODashboard() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [appraisals, setAppraisals] = useState([]);
  const [allAppraisals, setAllAppraisals] = useState([]);
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadData();
  }, [user]);

  const loadData = async () => {
    try {
      const [usersData, appraisalsData, feedbacksData] = await Promise.all([
        db.users.toArray(),
        db.appraisals.toArray(),
        db.feedbacks?.toArray() || []
      ]);
      
      setUsers(usersData.filter(u => u.status !== 'deleted'));
      setAllAppraisals(appraisalsData);
      // CEO sees appraisals assigned by them OR sent to them for review
      const ceoAppraisals = appraisalsData.filter(a => 
        a.assignedByCeo === user?.id || 
        a.status === 'sent_to_ceo' ||
        a.ceoId === user?.id
      );
      setAppraisals(ceoAppraisals);
      setFeedbacks(feedbacksData.filter(f => f.receiverId === user?.id));
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const activeUsers = users.filter(u => u.role !== 'admin');
  const supervisors = users.filter(u => u.role === 'supervisor');
  const employees = users.filter(u => u.role === 'employee');
  const pendingCeoReview = appraisals.filter(a => a.status === 'sent_to_ceo');
  const assignedAppraisals = appraisals.filter(a => a.assignedByCeo === user?.id);
  const completedAppraisals = appraisals.filter(a => a.status === 'finalized');
  const overdueAppraisals = allAppraisals.filter(a => isOverdue(a.dueDate) && a.status !== 'finalized');
  const inProgressAppraisals = allAppraisals.filter(a => ['ready_to_evaluate', 'employee_completed', 'manager_review'].includes(a.status));

  const getUserById = (id) => users.find(u => u.id === id);

  // Get upcoming birthdays and anniversaries
  const upcomingBirthdays = getUpcomingBirthdays(activeUsers, 14);
  const upcomingAnniversaries = getUpcomingAnniversaries(activeUsers, 30);

  // Department data
  const departments = [...new Set(users.map(u => u.profile?.department).filter(Boolean))];
  const departmentData = departments.map(dept => ({
    name: dept.length > 10 ? dept.substring(0, 10) + '...' : dept,
    fullName: dept,
    employees: users.filter(u => u.profile?.department === dept && u.role !== 'admin').length,
    appraisals: allAppraisals.filter(a => {
      const appUser = users.find(u => u.id === a.userId);
      return appUser?.profile?.department === dept;
    }).length,
    completed: allAppraisals.filter(a => {
      const appUser = users.find(u => u.id === a.userId);
      return appUser?.profile?.department === dept && a.status === 'finalized';
    }).length
  }));

  // Status distribution for all appraisals
  const statusData = [
    { name: 'Finalized', value: allAppraisals.filter(a => a.status === 'finalized').length },
    { name: 'Pending CEO', value: pendingCeoReview.length },
    { name: 'Manager Review', value: allAppraisals.filter(a => a.status === 'manager_review').length },
    { name: 'Employee Completed', value: allAppraisals.filter(a => a.status === 'employee_completed').length },
    { name: 'In Progress', value: allAppraisals.filter(a => a.status === 'ready_to_evaluate').length },
    { name: 'Overdue', value: overdueAppraisals.length },
  ].filter(d => d.value > 0);

  // Performance chart data - top performers
  const performanceData = completedAppraisals
    .sort((a, b) => (b.scores?.finalWeightedScore || 0) - (a.scores?.finalWeightedScore || 0))
    .slice(0, 10)
    .map(a => {
      const emp = getUserById(a.userId);
      return {
        name: `${emp?.profile?.firstName?.[0] || ''}${emp?.profile?.lastName?.[0] || ''}`,
        fullName: `${emp?.profile?.firstName || ''} ${emp?.profile?.lastName || ''}`,
        score: Number((a.scores?.finalWeightedScore || 0).toFixed(2)),
        department: emp?.profile?.department || 'N/A'
      };
    });

  // Monthly trend data (mock for visualization)
  const monthlyTrend = [
    { month: 'Jul', completed: 12, started: 18 },
    { month: 'Aug', completed: 15, started: 20 },
    { month: 'Sep', completed: 18, started: 22 },
    { month: 'Oct', completed: 14, started: 19 },
    { month: 'Nov', completed: 20, started: 25 },
    { month: 'Dec', completed: completedAppraisals.length, started: allAppraisals.length },
  ];

  // Calculate completion rate
  const completionRate = allAppraisals.length > 0 
    ? Math.round((allAppraisals.filter(a => a.status === 'finalized').length / allAppraisals.length) * 100) 
    : 0;

  // Average score
  const avgScore = completedAppraisals.length > 0
    ? (completedAppraisals.reduce((sum, a) => sum + (a.scores?.finalWeightedScore || 0), 0) / completedAppraisals.length).toFixed(2)
    : 0;

  if (loading) {
    return <div className="flex items-center justify-center h-64"><span className="text-gray-500">Loading...</span></div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900"> Dashboard</h1>
          <p className="text-gray-500 mt-1">Complete overview of organizational performance</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/ceo/feedback')}>
            <MessageSquare className="w-4 h-4 mr-2" /> Feedback
          </Button>
          <Button onClick={() => navigate('/ceo/assign')} className="bg-emerald-600 hover:bg-emerald-700 text-white">
            <UserPlus className="w-4 h-4 mr-2" /> Assign Appraisal
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/ceo/appraisals?filter=pending')}>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{pendingCeoReview.length}</p>
                <p className="text-xs text-gray-500">Pending Review</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/ceo/appraisals')}>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-50 rounded-lg flex items-center justify-center">
                <ClipboardCheck className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{assignedAppraisals.length}</p>
                <p className="text-xs text-gray-500">Appraisal Assigned</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center">
                <Target className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{completionRate}%</p>
                <p className="text-xs text-gray-500">Completion Rate</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-violet-50 rounded-lg flex items-center justify-center">
                <Award className="w-5 h-5 text-violet-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{avgScore}</p>
                <p className="text-xs text-gray-500">Avg Score</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm hover:shadow-md transition-shadow" onClick={() => navigate('/ceo/team')}>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-cyan-50 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-cyan-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{supervisors.length}</p>
                <p className="text-xs text-gray-500">Supervisors</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm hover:shadow-md transition-shadow" onClick={() => navigate('/ceo/team')}>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-rose-50 rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5 text-rose-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{employees.length}</p>
                <p className="text-xs text-gray-500">Employees</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-gray-100 p-1">
          <TabsTrigger value="overview" className="data-[state=active]:bg-white">Overview</TabsTrigger>
          <TabsTrigger value="performance" className="data-[state=active]:bg-white">Performance</TabsTrigger>
          <TabsTrigger value="team" className="data-[state=active]:bg-white">People</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Pending CEO Reviews */}
            <Card className="border-0 shadow-sm lg:col-span-2">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Clock className="w-5 h-5 text-amber-500" />
                    Awaiting Your Review
                  </CardTitle>
                  <Button variant="ghost" size="sm" className="text-indigo-600" onClick={() => navigate('/ceo/appraisals?filter=pending')}>
                    View All <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {pendingCeoReview.length > 0 ? (
                  <div className="space-y-3">
                    {pendingCeoReview.slice(0, 5).map(appraisal => {
                      const employee = getUserById(appraisal.userId);
                      const supervisor = getUserById(appraisal.managerId);
                      
                      return (
                        <div
                          key={appraisal.id}
                          className="flex items-center justify-between p-4 border border-gray-100 rounded-xl hover:border-indigo-200 hover:bg-indigo-50/30 transition-all cursor-pointer"
                          onClick={() => navigate(`/ceo/appraisals/${appraisal.id}`)}
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-11 h-11 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-full flex items-center justify-center text-white font-medium">
                              {employee?.profile?.firstName?.[0]}{employee?.profile?.lastName?.[0]}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">
                                {employee?.profile?.firstName} {employee?.profile?.lastName}
                              </p>
                              <p className="text-sm text-gray-500">{appraisal.title}</p>
                              <div className="flex items-center gap-3 mt-1">
                                <span className="text-xs text-gray-400">
                                  Reviewed by: {supervisor?.profile?.firstName} {supervisor?.profile?.lastName}
                                </span>
                                <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                                  Score: {appraisal.scores?.finalWeightedScore?.toFixed(1) || 'N/A'}
                                </Badge>
                              </div>
                            </div>
                          </div>
                          <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white">
                            Review
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <ClipboardCheck className="w-8 h-8 text-emerald-600" />
                    </div>
                    <p className="text-gray-900 font-medium">All caught up!</p>
                    <p className="text-sm text-gray-500 mt-1">No appraisals pending your review</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Status Distribution */}
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <PieChartIcon className="w-5 h-5 text-indigo-500" />
                  Status Overview
                </CardTitle>
              </CardHeader>
              <CardContent>
                {statusData.length > 0 ? (
                  <>
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie
                          data={statusData}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={80}
                          paddingAngle={3}
                          dataKey="value"
                        >
                          {statusData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value, name) => [value, name]} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="grid grid-cols-2 gap-2 mt-4">
                      {statusData.map((item, i) => (
                        <div key={item.name} className="flex items-center gap-2 text-xs">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                          <span className="text-gray-600 truncate">{item.name}: {item.value}</span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8 text-gray-500">No appraisal data yet</div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Department Overview & Trend */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-teal-500" />
                  Department Overview
                </CardTitle>
              </CardHeader>
              <CardContent>
                {departmentData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={departmentData} layout="vertical" margin={{ left: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={80} />
                      <Tooltip 
                        formatter={(value, name) => [value, name === 'employees' ? 'Employees' : name === 'completed' ? 'Completed' : 'Appraisals']}
                        labelFormatter={(label) => departmentData.find(d => d.name === label)?.fullName || label}
                      />
                      <Legend />
                      <Bar dataKey="employees" name="Employees" fill="#0d9488" radius={[0, 4, 4, 0]} />
                      <Bar dataKey="completed" name="Completed" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center py-8 text-gray-500">No department data</div>
                )}
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-emerald-500" />
                  Monthly Trend
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={monthlyTrend}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend />
                    <Area type="monotone" dataKey="started" name="Started" stroke="#8b5cf6" fill="#8b5cf680" />
                    <Area type="monotone" dataKey="completed" name="Completed" stroke="#10b981" fill="#10b98180" />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="performance" className="space-y-6">
          {/* Top Performers */}
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Award className="w-5 h-5 text-amber-500" />
                Top Performers
              </CardTitle>
            </CardHeader>
            <CardContent>
              {performanceData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={performanceData}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis domain={[0, 5]} tick={{ fontSize: 11 }} />
                    <Tooltip 
                      formatter={(value) => [value.toFixed(2), 'Score']}
                      labelFormatter={(label) => {
                        const item = performanceData.find(d => d.name === label);
                        return item ? `${item.fullName} (${item.department})` : label;
                      }}
                    />
                    <Bar dataKey="score" fill="#6366f1" radius={[4, 4, 0, 0]}>
                      {performanceData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={index === 0 ? '#f59e0b' : index === 1 ? '#8b5cf6' : '#6366f1'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-12 text-gray-500">No completed appraisals yet</div>
              )}
            </CardContent>
          </Card>

          {/* Performance Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="border-0 shadow-sm bg-gradient-to-br from-emerald-50 to-teal-50">
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <TrendingUp className="w-8 h-8 text-emerald-600" />
                  </div>
                  <p className="text-3xl font-bold text-emerald-700">{completedAppraisals.length}</p>
                  <p className="text-sm text-gray-600 mt-1">Completed Reviews</p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm bg-gradient-to-br from-amber-50 to-orange-50">
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Clock className="w-8 h-8 text-amber-600" />
                  </div>
                  <p className="text-3xl font-bold text-amber-700">{inProgressAppraisals.length}</p>
                  <p className="text-sm text-gray-600 mt-1">In Progress</p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm bg-gradient-to-br from-rose-50 to-pink-50">
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <AlertTriangle className="w-8 h-8 text-rose-600" />
                  </div>
                  <p className="text-3xl font-bold text-rose-700">{overdueAppraisals.length}</p>
                  <p className="text-sm text-gray-600 mt-1">Overdue</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="team" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Upcoming Birthdays */}
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Cake className="w-5 h-5 text-pink-500" />
                    Upcoming Birthdays
                  </CardTitle>
                  <Badge variant="secondary">{upcomingBirthdays.length}</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {upcomingBirthdays.length > 0 ? (
                  upcomingBirthdays.slice(0, 5).map(u => (
                    <div key={u.id} className="flex items-center justify-between p-3 bg-gradient-to-r from-pink-50 to-rose-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-gradient-to-br from-pink-400 to-rose-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
                          {u.profile?.firstName?.[0]}{u.profile?.lastName?.[0]}
                        </div>
                        <div>
                          <p className="font-medium text-sm text-gray-900">{u.profile?.firstName} {u.profile?.lastName}</p>
                          <p className="text-xs text-gray-500">{u.profile?.department}</p>
                        </div>
                      </div>
                      <p className="text-xs text-gray-400">{formatDate(u.profile?.dateOfBirth, 'MMM d')}</p>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">No birthdays this week</p>
                )}
              </CardContent>
            </Card>

            {/* Work Anniversaries */}
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <CalendarDays className="w-5 h-5 text-violet-500" />
                    Work Anniversaries
                  </CardTitle>
                  <Badge variant="secondary">{upcomingAnniversaries.length}</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {upcomingAnniversaries.length > 0 ? (
                  upcomingAnniversaries.slice(0, 5).map(u => (
                    <div key={u.id} className="flex items-center justify-between p-3 bg-gradient-to-r from-violet-50 to-purple-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-gradient-to-br from-violet-400 to-purple-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
                          {u.profile?.firstName?.[0]}{u.profile?.lastName?.[0]}
                        </div>
                        <div>
                          <p className="font-medium text-sm text-gray-900">{u.profile?.firstName} {u.profile?.lastName}</p>
                          <p className="text-xs text-gray-500">{getYearsOfService(u.profile?.hireDate)} years</p>
                        </div>
                      </div>
                      <p className="text-xs text-gray-400">{formatDate(u.profile?.hireDate, 'MMM d')}</p>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">No anniversaries this month</p>
                )}
              </CardContent>
            </Card>

            {/* Recent Feedback */}
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-indigo-500" />
                    Recent Feedback
                  </CardTitle>
                  <Button variant="ghost" size="sm" className="text-indigo-600" onClick={() => navigate('/ceo/feedback')}>
                    View All
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {feedbacks.length > 0 ? (
                  feedbacks.slice(0, 4).map(fb => (
                    <div key={fb.id} className="p-3 border border-gray-100 rounded-lg">
                      <div className="flex items-start justify-between">
                        <p className="text-sm font-medium text-gray-900">{fb.anonymous ? 'Anonymous' : 'Team member'}</p>
                        <Badge variant="secondary" className="capitalize text-xs">{fb.type}</Badge>
                      </div>
                      <p className="text-sm text-gray-600 mt-1 line-clamp-2">{fb.message}</p>
                      <p className="text-xs text-gray-400 mt-2">{new Date(fb.createdAt).toLocaleDateString()}</p>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">No feedback yet</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Team Overview by Role */}
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Organization Structure</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Supervisors ({supervisors.length})</h4>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {supervisors.map(s => (
                      <div key={s.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50">
                        <div className="w-8 h-8 bg-gradient-to-br from-teal-400 to-cyan-500 rounded-full flex items-center justify-center text-white text-xs font-medium">
                          {s.profile?.firstName?.[0]}{s.profile?.lastName?.[0]}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">{s.profile?.firstName} {s.profile?.lastName}</p>
                          <p className="text-xs text-gray-500">{s.profile?.department}</p>
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {employees.filter(e => e.supervisorId === s.id).length} reports
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Departments ({departments.length})</h4>
                  <div className="space-y-2">
                    {departments.slice(0, 6).map(dept => (
                      <div key={dept} className="flex items-center justify-between p-2 rounded-lg bg-gray-50">
                        <span className="text-sm text-gray-700">{dept}</span>
                        <Badge variant="secondary">
                          {users.filter(u => u.profile?.department === dept && u.role !== 'admin').length}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
